import { MoreVert } from '@mui/icons-material';
import { Box, Menu } from '@mui/material';
import { GridActionsCellItem, GridPaginationModel } from '@mui/x-data-grid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  setEntity,
  setPage,
  setPageSize,
  setViewPanel,
} from '../../../app/approvalQueue/approvalQueueSlice';
import { useGetApiApprovalQueueQuery } from '../../../app/athenaApiGen';
import { RootState } from '../../../app/store';
import CustomNoRows from '../../../components/DataGrid/CustomNoRows';
import { StyledDataGrid } from '../../../shared-theme/StyledComponents';
import useDebounce from '../../../useDebounce';
import { TABLE_PAGE_SIZE_OPTIONS } from '../../../utils/tableVariables';
import { APPROVAL_QUEUE_COLS } from '../consts';
import ActionsMenu from './ActionsMenu';

type TApprovalQueueTableProps = {
  approvalState?: 'all' | 'pending' | 'approved' | 'rejected' | 'in-recovery';
  // When set, the table uses this fixed page size (e.g. the compact home
  // widget) instead of the shared Redux pageSize, and locks the selector.
  fixedPageSize?: number;
};

declare module '@mui/x-data-grid' {
  interface BasePaginationPropsOverrides {
    showFirstButton: boolean;
    showLastButton: boolean;
  }
}

// Empty state for the approval queue: the generic "Try adjusting your
// filters or adding new data" message reads as a prompt to do
// something when there's genuinely nothing to do.
const ApprovalQueueNoRows = () => (
  <CustomNoRows
    title="You’re all caught up."
    subtitle="Nothing here needs your action right now."
  />
);

const ApprovalQueueTable = (props: TApprovalQueueTableProps) => {
  const { page, pageSize, selectedEntity, viewPanel } = useSelector(
    (state: RootState) => state.approvalQueue,
  );
  const effectivePageSize = props.fixedPageSize ?? pageSize;
  const { data, isLoading } = useGetApiApprovalQueueQuery(
    {
      page: page + 1,
      limit: effectivePageSize,
      approvalState: props.approvalState,
    },
    { refetchOnMountOrArgChange: true },
  );
  const dispatch = useDispatch();
  const location = useLocation();
  const state = location.state;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenu, setOpenMenu] = useState<boolean>(false);

  const rowCountRef = useRef(data?.totalCount || 0);

  const rowCount = useMemo(() => {
    if (data?.totalCount !== undefined) {
      rowCountRef.current = data.totalCount;
    }
    return rowCountRef.current;
  }, [data?.totalCount]);

  const handleClose = () => {
    setOpenMenu(false);
    setAnchorEl(null);
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page));
    // Don't write back a fixed (widget) page size to the shared Redux state.
    if (props.fixedPageSize === undefined) {
      dispatch(setPageSize(model.pageSize));
    }
  };

  const debouncePagination = useDebounce(handlePaginationChange, 300);

  useEffect(() => {
    if (state?.id) {
      dispatch(setViewPanel(true));
    }
  }, [state?.id]);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        height: '100%',
        p: 0,
        boxSizing: 'border-box',
      }}
    >
      <StyledDataGrid
        sx={{
          height: '100%',
          '& .MuiDataGrid-footerContainer': {
            paddingBottom: 0,
          },
          '& .MuiTablePagination-root': {
            marginBottom: 0,
          },
        }}
        paginationMode="server"
        pagination
        pageSizeOptions={
          props.fixedPageSize ? [props.fixedPageSize] : TABLE_PAGE_SIZE_OPTIONS
        }
        loading={isLoading}
        paginationModel={{
          page: page,
          pageSize: effectivePageSize,
        }}
        slotProps={{
          basePagination: {
            showFirstButton: true,
            showLastButton: true,
          },
        }}
        onPaginationModelChange={debouncePagination}
        rowCount={rowCount}
        onCellClick={({ row, colDef }) => {
          dispatch(setEntity(row));

          //short the function, because we do not need to explicitly open the view panel on a click in the action column
          if (colDef.field === 'actions') return;

          if (row.workflowId === selectedEntity?.workflowId) {
            dispatch(setViewPanel(!viewPanel));
          } else if (!viewPanel) {
            dispatch(setViewPanel(true));
          }
        }}
        rows={data?.data ?? []}
        getRowId={(item) =>
          `${item.workflowId ?? '1'}-${item.workflowTaskNumber ?? ''}`
        }
        columns={[
          ...APPROVAL_QUEUE_COLS,
          {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: () => {
              return [
                <GridActionsCellItem
                  icon={<MoreVert />}
                  label="Actions"
                  id="approval-queue-action-button"
                  onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                    setOpenMenu(true);
                  }}
                  color="inherit"
                />,
              ];
            },
          },
        ]}
        slots={{
          // Wrap CustomNoRows so we can bake in approval-queue-specific
          // copy without fighting MUI's NoRowsOverlay slot-props typing,
          // which doesn't expose arbitrary props on the overlay component.
          noRowsOverlay: ApprovalQueueNoRows,
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': 'approval-queue-action-button',
          },
        }}
      >
        <ActionsMenu handleClose={handleClose} />
      </Menu>
    </Box>
  );
};

export default ApprovalQueueTable;
